/**
 * App-wide confirmation gate. A tiny framework-agnostic store so non-React code
 * (the axios interceptor) can request a confirmation modal and await the user's
 * choice. `<ConfirmRoot/>` (mounted once in Providers) renders the dialog.
 *
 * Used by src/utils/api.ts to confirm every PUT/PATCH (modifying existing data)
 * across the whole app, and can also be called directly: `await confirm({...})`.
 */

export interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

interface Pending { opts: ConfirmOptions; resolve: (v: boolean) => void }

let current: Pending | null = null;
const queue: Pending[] = [];
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

function advance() {
  if (!current && queue.length) {
    current = queue.shift()!;
    emit();
  }
}

/** Request a confirmation. Resolves true (confirmed) / false (cancelled). */
export function confirm(opts: ConfirmOptions = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    queue.push({ opts, resolve });
    advance();
  });
}

/** Resolve the active confirmation (called by ConfirmRoot's buttons). */
export function resolveConfirm(value: boolean) {
  if (!current) return;
  const { resolve } = current;
  current = null;
  emit();
  resolve(value);
  advance();
}

export function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function getSnapshot(): Pending | null {
  return current;
}
